import React from "react";
import { Dimensions, StyleSheet, View, Modal, Text, TextInput, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "react-native-toast-notifications";

import Spinner from "../../../components/shared/Spinner";
import {
  getFactoryOrderDetailsByKey,
  interruptRunOrderDetail,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useTranslation } from "../../../utils/useTranslation";

interface Props {
  visible: boolean;
  onClose: () => void;
  runOrder: any;
}

const ChinaTrackerInterruptRunOrder: React.FC<Props> = ({
  visible,
  onClose,
  runOrder: order,
}) => {
  const dispatch = useDispatch<any>();
  const toast = useToast();
  const { loadingInterruptsAction } = useSelector(
    (state: any) => state.chinaTracker
  );

  const isInterrupted = !!order?.interrupted;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { t } = useTranslation();

  /**
   * FINAL SUBMIT
   */
  const submitAction = (data: any) => {
    const interruptStatus = !order.interrupted;
    const payload = {
      factoryOrderDetail: order._id,
      message: data.message,
      interruptType: interruptStatus ? "INTERRUPT" : "UNINTERRUPT",
    };

    dispatch(interruptRunOrderDetail(payload))
      .unwrap()
      .then(() => {
        toast.show(
          isInterrupted
            ? t("runOrderResumedSuccess")
            : t("runOrderInterruptedSuccess"),
          { type: "success" }
        );
        dispatch(getFactoryOrderDetailsByKey(order.key)).unwrap();
      })
      .catch((err: any) =>
        toast.show(err?.message || t("actionFailed"), { type: "danger" })
      )
      .finally(onClose);
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Header */}
            <View style={styles.header}>
              <TouchableOpacity onPress={onClose} style={styles.backButton}>
                <Text style={styles.backArrow}>←</Text>
              </TouchableOpacity>
              <Text
                style={[
                  styles.title,
                  isInterrupted ? styles.resumeTitle : styles.interruptTitle,
                ]}
              >
                {isInterrupted ? t("resumeRunOrder") : t("interruptRunOrder")}
              </Text>
            </View>

            {/* Notice */}
            <View
              style={[
                styles.noticeBox,
                isInterrupted ? styles.resumeBox : styles.interruptBox,
              ]}
            >
              <Text style={styles.noticeText}>
                {isInterrupted
                  ? t("resumeRunOrderNotice")
                  : t("interruptRunOrderNotice")}
              </Text>
            </View>

            {loadingInterruptsAction ? (
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ marginVertical: 30 }}
              />
            ) : (
              <>
                {/* Order Details */}
                <View style={styles.detailsContainer}>
                  <Detail label={t("orderNumber")} value={order?.orderNumber} />
                  <Detail
                    label={t("plannedQuantity")}
                    value={order?.plannedQty?.toLocaleString()}
                  />
                  <Detail label={t("duration")} value={order?.duration} />
                  <Detail
                    label={t("productionLine")}
                    value={order?.allocateProductionLine}
                  />
                  <Detail label={t("runOrder")} value={order?.runOrder} />
                </View>

                {/* Message */}
                <View style={styles.messageSection}>
                  <Text style={styles.inputLabel}>{t("message")}</Text>
                  <Controller
                    control={control}
                    name="message"
                    rules={{ required: t("messageRequired") }}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <TextInput
                          style={styles.messageInput}
                          multiline
                          placeholder={
                            isInterrupted
                              ? t("explainResumeRunOrder")
                              : t("explainInterruptRunOrder")
                          }
                          value={value}
                          onChangeText={onChange}
                        />
                        {errors.message && (
                          <Text style={styles.errorText}>
                            {errors.message.message as string}
                          </Text>
                        )}
                      </>
                    )}
                  />
                </View>

                {/* Footer */}
                <View style={styles.footer}>
                  <TouchableOpacity
                    style={[
                      styles.sendButton,
                      isInterrupted ? styles.resumeBtn : styles.interruptBtn,
                    ]}
                    onPress={handleSubmit(submitAction)}
                  >
                    <Text style={styles.sendText}>
                      {isInterrupted ? t("resume") : t("interrupt")}
                    </Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

/**
 * Small Detail Component
 */
const Detail = ({ label, value }: any) => (
  <View style={styles.detailRow}>
    <Text style={styles.label}>{label}:</Text>
    <Text style={styles.value}>{value || "-"}</Text>
  </View>
);

const { width } = Dimensions.get("window");

/* ===================== STYLES ===================== */

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalContainer: {
    backgroundColor: "#fff",
    width: "65%",
    height: "65%",
    borderRadius: 12,
    padding: 20,
  },
  scrollContent: {
    flexGrow: 1,
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  backButton: {
    marginRight: 10,
  },
  backArrow: {
    fontSize: 24,
    fontWeight: "bold",
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
  },

  interruptTitle: { color: "#C62828" },
  resumeTitle: { color: "#2E7D32" },

  noticeBox: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
  },
  interruptBox: {
    backgroundColor: "#FDECEA",
    borderLeftWidth: 4,
    borderLeftColor: "#D32F2F",
  },
  resumeBox: {
    backgroundColor: "#E8F5E9",
    borderLeftWidth: 4,
    borderLeftColor: "#388E3C",
  },
  noticeText: {
    fontSize: 14,
    color: "#333",
  },

  detailsContainer: {
    marginBottom: 20,
  },
  detailRow: {
    flexDirection: "row",
    marginBottom: 10,
  },
  label: {
    width: width > 400 ? 140 : 110,
    fontWeight: "600",
  },
  value: {
    flex: 1,
  },

  messageSection: {
    flex: 1,
    marginBottom: 20,
  },
  inputLabel: {
    fontWeight: "600",
    marginBottom: 8,
  },
  messageInput: {
    minHeight: 120,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#fafafa",
  },

  footer: {
    alignItems: "flex-end",
  },
  sendButton: {
    paddingHorizontal: 30,
    paddingVertical: 12,
    borderRadius: 6,
  },
  interruptBtn: {
    backgroundColor: "#D32F2F",
  },
  resumeBtn: {
    backgroundColor: "#388E3C",
  },
  sendText: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 16,
  },

  errorText: {
    color: "red",
    fontSize: 12,
    marginTop: 5,
  },
});

export default ChinaTrackerInterruptRunOrder;
